import React, { useState, useEffect, useContext, useRef } from 'react';
import { Alert, Col, Container, Row } from 'react-bootstrap';
import { useParams, useHistory } from "react-router-dom";
import { Paper, Typography } from '@material-ui/core';
import AppContext from 'Utils/AppContext';
import { filterTableResponseForDisplay } from 'Utils/entityLevels';
import LevelCrumbs from '../LevelCrumbs';
import TableEntityCard from './TableEntityCard';
import TableEntityBarchart from './TableEntityBarchart';
import TableEntityFeed from './TableEntityFeed';
import { getTablePanelData } from './TablePanelUtils';
import { getTableResult, clearDiscoverResultsCache, mapTableResultToFoundEntitiesShape } from 'DataDiscovery/discoverApi';
import FetchContainer from 'Utils/FetchContainer';
import { GoFileZip } from 'react-icons/go';
import {
  archiveInnerPath,
  isArchivePath,
  splitArchivePath,
} from 'Utils/archivePath';
import { isStructuredTreeType } from 'Utils/fileTypeUtils';
import { getSourceNomenclature } from 'Utils/nomenclature';

const TablePanel = (props) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [filteredByCol, setFilteredByCol] = useState(null);
  const [filteredOverview, setFilteredOverview] = useState({});
  const [filteredEntityTotal, setFilteredEntityTotal] = useState(0);
  const [dataLabelSingular, setDataLabelSingular] = useState('');
  const [foundEntities, setFoundEntities] = useState({});
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const { entityLevels } = useContext(AppContext);
  const params = useParams();
  // Optional-prop fallback so this component is usable both as the
  // `:entity/:table+` route element (params only) and directly rendered from
  // the bare `/results/:search` route for a single-container run (A1 landing
  // rule), which has no matching :entity/:table path segments to read.
  const table = props.table ?? params.table;
  const searchid = props.searchid ?? params.search;
  const entity = props.entity ?? params.entity;
  const setError = props.setError;
  const inProgress = props.inProgress;
  const sampledRowCount = foundEntities.rows_count;

  const fetchDataHandler = async () => {
    if (table) {
      try {
        const response = await getTableResult(searchid, table);
        // Drop HIDDEN entities install-wide before mapping (REPORT_ONLY stays visible here).
        const filtered = filterTableResponseForDisplay(response, entityLevels);
        const foundEntitiesValue = mapTableResultToFoundEntitiesShape(filtered);
        const {
          dataValue,
          totalValue,
          filteredOverviewValue,
          filteredEntityTotal: entityTotalValue
        } = getTablePanelData(foundEntitiesValue, table, entity, filteredByCol, foundEntitiesValue.rows_count);
        setFoundEntities(foundEntitiesValue);
        setFilteredOverview(filteredOverviewValue);
        setFilteredEntityTotal(entityTotalValue);
        setData(dataValue);
        setTotal(totalValue);
      } catch (e) {
        console.log(e);
        setError(e?.message);
      }
    }
  };
  
  // partialTick bumps when ReportPanel's slow partial-results poll lands a fresh snapshot;
  // the refetch reads the results cache that poll just warmed (no extra payload transfer).
  const partialTick = props.partialTick;
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchDataHandler();
      setLoading(false);
    }
    init();
  }, [table, entity, filteredByCol, partialTick]);

  // Do NOT poll while the run is in progress — getTableResult goes through getOverview, so
  // each 10s tick re-downloaded the whole multi-MB overview payload (see OverviewPanel for the
  // full rationale, audit R6 / §3 UI). Fetch exactly once when the run transitions out of
  // progress; the App-level status poll keeps progress live in the meantime.
  const wasInProgress = useRef(false);
  useEffect(() => {
    if (wasInProgress.current && !inProgress && searchid) {
      clearDiscoverResultsCache(searchid);
      fetchDataHandler();
    }
    wasInProgress.current = !!inProgress;
  }, [inProgress, searchid]);

  const resetFilter=async() => {
    history.push(`/results/${searchid}/ALL/${table}`);
    filterData(null);
  }

  const filterData = (field) => {
    if (field) {
      let totalValue = 0;
      let filteredOverviewValue = {};
      const dataValue = JSON.parse(JSON.stringify(data));
      const dataValueChildren = [...dataValue.children];
      const filteredDataValueChildren = dataValueChildren.filter(item => item.name === field);
      const childrenValues = filteredDataValueChildren[0].children;
      dataValue.children = filteredDataValueChildren;

      for (let i = 0; i < childrenValues.length; i++) {
        let value = childrenValues[i].value;
        let name = childrenValues[i].name;
        totalValue += value;
        filteredOverviewValue[name] = filteredOverviewValue[name] + value || value;
      }
      
      setFilteredOverview(filteredOverviewValue);
      setFilteredEntityTotal(
        entity !== 'ALL' ? (filteredOverviewValue[entity] || 0) : 0
      );
      setTotal(totalValue);
      setData(dataValue);
      setFilteredByCol(field);
    } else {
      const {
        dataValue,
        totalValue,
        filteredOverviewValue,
        filteredEntityTotal: entityTotalValue
      } = getTablePanelData(foundEntities, table, entity, null, foundEntities.rows_count);

      setFilteredOverview(filteredOverviewValue);
      setFilteredEntityTotal(entityTotalValue);
      setData(dataValue);
      setTotal(totalValue);
      setFilteredByCol(null);
    }
  }

  useEffect(() => {
    const dataLabel = props.dataLabel;
    let dataLabelSingularValue = '';

    if (dataLabel && dataLabel[dataLabel.length - 1] === 's') {
      dataLabelSingularValue = dataLabel.substring(0, dataLabel.length - 1);
    }
    setDataLabelSingular(dataLabelSingularValue);
  }, [props.dataLabel]);


  // Source format for this "table": the row's own sniffed sampler_type when present
  // (Folder Search chunk rows, sniff-divergent typed rows), else the run's data-source
  // type. JSON/XML files are whole-file trees — render them through the structured view
  // rather than letting the field-name heuristic mistake top-level keys for columns.
  const structuredSource = isStructuredTreeType(
    foundEntities._sampler_type || props.datasourceType
  );

  // console.log('FILTERS:',filteredOverview,filteredByCol,entity)
  const meta = foundEntities && foundEntities._meta;
  const timeoutBatches = foundEntities && foundEntities._timeout_batches;
  const modelErrorBatches = foundEntities && foundEntities._model_error_batches;
  const errorMessage = foundEntities && foundEntities._error_message;
  return (
    <FetchContainer absolute={true} fetchingData={loading}>
      <div>
        {errorMessage && (
          <Row className="widget-row">
            <Col>
              <Alert variant='danger' className='mb-3'>
                <Typography variant='body2'>
                  This {dataLabelSingular || 'table'} failed to process, so the results below are partial
                  or absent: {errorMessage}
                </Typography>
              </Alert>
            </Col>
          </Row>
        )}
        {timeoutBatches > 0 && (
          <Row className="widget-row">
            <Col>
              <Alert variant='warning' className='mb-3'>
                <Typography variant='body2'>
                  Results are partial: {timeoutBatches} batch{timeoutBatches > 1 ? 'es' : ''} timed out
                  during model inference and {timeoutBatches > 1 ? 'were' : 'was'} skipped.
                </Typography>
              </Alert>
            </Col>
          </Row>
        )}
        {modelErrorBatches > 0 && (
          <Row className="widget-row">
            <Col>
              <Alert variant='warning' className='mb-3'>
                <Typography variant='body2'>
                  Results are partial: the model rejected {modelErrorBatches} batch{modelErrorBatches > 1 ? 'es' : ''} (client
                  error), so those rows were not scanned.
                </Typography>
              </Alert>
            </Col>
          </Row>
        )}
        {meta && meta.depth_truncated && (
          <Row className="widget-row">
            <Col>
              <Alert variant='warning' className='mb-3'>
                <Typography variant='body2'>
                  Some paths were not scanned: the maximum depth of{' '}
                  {meta.max_depth_reached} was reached.
                  {Array.isArray(meta.truncated_paths) && meta.truncated_paths.length > 0 &&
                    ` (${meta.truncated_paths.length} path${meta.truncated_paths.length > 1 ? 's' : ''} affected)`}
                </Typography>
                <Typography variant='caption' color='textSecondary'>
                  Set <code>discovery.json.full-depth=true</code> to scan all levels.
                </Typography>
                {Array.isArray(meta.truncated_paths) && meta.truncated_paths.length > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12 }}>
                      Show affected paths
                    </summary>
                    <ul style={{ fontSize: 12, margin: '4px 0 0 16px' }}>
                      {meta.truncated_paths.map((p) => <li key={p}>{p}</li>)}
                    </ul>
                  </details>
                )}
              </Alert>
            </Col>
          </Row>
        )}
        <Row className="widget-row">
          <Col>
            <LevelCrumbs
              path={'/results'}
              activeCrumb='table'
              searchid={searchid}
              entity={entity}
              table={table}
              // Qualified route param (schema.table): crumb shows the bare table name;
              // the schema renders as its own badge alongside Source.
              tableLabel={
                foundEntities && foundEntities._schema
                  && table && table.indexOf(`${foundEntities._schema}.`) === 0
                  ? table.slice(foundEntities._schema.length + 1)
                  : undefined
              }
              connection={props.connection}
              schema={foundEntities && foundEntities._schema}
              // Badge noun for the qualifier: "Schema" for relational, "Database" for
              // MongoDB (whose qualified keys carry the database name — Mongo has no
              // schema level).
              schemaLabel={(() => {
                const n = getSourceNomenclature(props.engine, props.datasourceType);
                return n.containerSchema || n.container;
              })()}
              filteredByCol={filteredByCol}
              resetFilter={() => resetFilter()}
            />
          </Col>
        </Row>
        {isArchivePath(table) && (
          <Row className="widget-row">
            <Col>
              <ArchivePathDetail value={table} />
            </Col>
          </Row>
        )}
        { data && 
          <Container>
            <Row className="widget-row">
              <Col>
                <TableEntityBarchart 
                  dataLabel={dataLabelSingular}
                  filteredOverview={filteredOverview} 
                  filteredByCol={filteredByCol}
                  total={total}
                  searchid={searchid}
                  table={table}
                  entity={entity}
                  rowsSampled={foundEntities.rows_count}
                />
              </Col>
              <Col>
                <TableEntityCard
                  dataLabel={dataLabelSingular}
                  filteredOverview={filteredOverview}
                  filteredEntityTotal={filteredEntityTotal}
                  filteredByCol={filteredByCol}
                  filterDataHandler={(field) => filterData(field)} 
                  entity={entity}
                  data={data}
                  details={{}}
                  label={table}
                  table={table}
                  searchid={searchid}
                  total={total}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TableEntityFeed 
                  dataLabel={props.dataLabel}
                  total={total}
                  filteredByCol={filteredByCol}
                  filterDataHandler={(field) => filterData(field)} 
                  data={data.children}
                  searchid={searchid}  
                  entity={entity} 
                  table={table} 
                  fields={foundEntities[table]}
                  filteredOverview={filteredOverview}
                  isEncrypted={props.isEncrypted}
                  encryptedTables={props.encryptedTables}
                  sampledRowCount={foundEntities.rows_count}
                  columnsView={true}
                  structuredSource={structuredSource}
                />
              </Col>
            </Row>
          </Container>
        }
      </div>
      
    </FetchContainer>
  );
}

/**
 * Compact info panel rendered above the table-detail when the "table" is
 * actually an entry inside a multi-entry archive (zip/jar/...). Shows the
 * on-disk archive path and the entry path inside it as separate rows so the
 * user can copy either half independently.
 */
const ArchivePathDetail = ({ value }) => {
  const { outer } = splitArchivePath(value);
  const inner = archiveInnerPath(value);
  return (
    <Paper
      style={{
        padding: 12,
        marginBottom: 16,
        backgroundColor: '#E9EEFF',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <GoFileZip aria-hidden='true' />
        <Typography variant='body2' style={{ fontWeight: 600 }}>
          Inside archive
        </Typography>
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
        <div>
          <span style={{ color: '#5C6473' }}>Archive: </span>
          <span>{outer}</span>
        </div>
        <div>
          <span style={{ color: '#5C6473' }}>Entry: </span>
          <span>{inner || '(archive root)'}</span>
        </div>
        <div>
          <span style={{ color: '#5C6473' }}>Full path: </span>
          <span style={{ wordBreak: 'break-all' }}>{value}</span>
        </div>
      </div>
    </Paper>
  );
};

export default TablePanel;